<template>
  <div ref="chart" style="width: 100%; height: 70px; background-color: #F4F1FAFF;margin-bottom: 12px;"></div>
</template>

<script>
import * as echarts from 'echarts';
import { debounce } from 'lodash';

export default {
  name: 'LineChart',
  props: {
    lineColor: {
      type: String,
      default: '#B81AE7'
    },
    timeWindow: {
      type: Number,
      default: 50
    },
    refreshInterval: {
      type: Number,
      default: 1000
    },
    maxDataPoints: {
      type: Number,
      default: 500
    }
  },
  data() {
    return {
      chart: null,
      timeData: [],
      valueData: [],
      yMin: 0,
      yMax: 9999,
      debouncedUpdate: null
    };
  },
  mounted() {
    this.debouncedUpdate = debounce(this.updateChart, 50);
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart);
      this.updateChartOption();
    },

    handleResize() {
      this.chart && this.chart.resize();
    },

    updateChartOption() {
      const option = {
        title: { text: '' },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const date = new Date(params[0].value[0]);
            return `时间: ${date.toLocaleTimeString()}<br/>数值: ${params[0].value[1].toFixed(2)}`;
          }
        },
        grid: {
          top: 10,
          bottom: 15,
          left: 30,
          right: 10,
          containLabel: false
        },
        xAxis: {
          show: false,
          type: 'time',
          axisLabel: {
            formatter: (value) => new Date(value).toLocaleTimeString()
          },
          min: () => Date.now() - this.timeWindow * 1000,
          max: () => Date.now()
        },
        yAxis: {
          show: false,
          type: 'value',
          min: this.yMin,
          max: this.yMax,
          scale: true,
          boundaryGap: [0.1, 0.1],
          axisLabel: {
            formatter: function(value) {
              return value;
            }
          }
        },
        series: [{
          name: '数值',
          type: 'line',
          showSymbol: false,
          data: this.valueData,
          lineStyle: {
            color: this.lineColor,
            width: 2
          },
          step: 'middle',
          animation: true,
          animationDuration: 1000,
          animationEasing: 'linear'
        }]
      };
      this.chart.setOption(option);
    },

    // 动态调整Y轴范围
    adjustYAxis(minValue, maxValue) {
      if (this.chart) {
        this.chart.setOption({
          yAxis: {
            min: minValue,
            max: maxValue
          }
        });
      }
    },

    addDataPoint(value) {
      const timestamp = Date.now();
      this.valueData.push([timestamp, value]);

      // 更新Y轴范围
      this.updateYAxisRange();

      // 清理旧数据
      const cutoffTime = timestamp - this.timeWindow * 1000;
      while (this.valueData.length > 0 && this.valueData[0][0] < cutoffTime) {
        this.valueData.shift();
      }

      // 更新图表
      this.chart.setOption({
        series: [{
          data: this.valueData
        }]
      });

      // 动态调整Y轴范围
      const currentMin = this.chart.getOption().yAxis[0].min;
      const currentMax = this.chart.getOption().yAxis[0].max;

      if (value < currentMin || value > currentMax) {
        const newMin = Math.min(currentMin, value);
        const newMax = Math.max(currentMax, value);
        this.adjustYAxis(newMin, newMax);
      }
    },

    updateChart() {
      const timestamp = Date.now();
      const cutoffTime = timestamp - this.timeWindow * 1000;

      this.chart.setOption({
        xAxis: {
          min: cutoffTime,
          max: timestamp
        },
        series: [{
          data: this.valueData
        }]
      });
    },

    updateYAxisRange() {
      if (this.valueData.length === 0) return;

      const values = this.valueData.map(item => item[1]);
      const currentMin = Math.min(...values);
      const currentMax = Math.max(...values);

      // 确保Y轴范围在0-9999之间
      this.yMin = Math.max(0, currentMin - (currentMax - currentMin) * 0.1); // 留10%的余量
      this.yMax = Math.min(9999, currentMax + (currentMax - currentMin) * 0.1); // 留10%的余量但不超过9999

      // 如果所有值都相同，设置一个合理的范围
      if (currentMin === currentMax) {
        this.yMin = Math.max(0, currentMin - 10);
        this.yMax = Math.min(9999, currentMax + 10);
      }

      // 如果yMin和yMax仍然相同（比如值都是0）
      if (this.yMin === this.yMax) {
        this.yMax = this.yMin + 10;
      }

      this.chart.setOption({
        yAxis: {
          min: this.yMin,
          max: this.yMax
        }
      });
    }
  }
};
</script>




// WEBPACK FOOTER //
// src/pages/tcode/remote/LineChart.vue